import i18next, { type i18n } from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * Arabic and English with RTL/LTR support (spec sections 86 and 87). Both apps share these resources
 * so a term is translated once.
 */
export const SUPPORTED_LANGUAGES = ['en', 'ar'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export function isRtl(language: string): boolean {
  return language.startsWith('ar');
}

/** Applies direction and language to the document so CSS logical properties do the rest. */
export function applyDocumentDirection(language: string): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.lang = language;
  document.documentElement.dir = isRtl(language) ? 'rtl' : 'ltr';
}

/**
 * Sets the tab title in the language the reader chose.
 *
 * index.html carries a static English title, so the tab kept reading "Barah Pay — Admin" under an
 * otherwise fully Arabic portal. It is the one piece of user-visible text that a resource file alone
 * could not reach, because nothing rendered it.
 */
export function applyDocumentTitle(title: string): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.title = title;
}

const en = {
  common: {
    appName: 'Barah Pay',
    portalTitle: 'Barah Pay — Admin',
    search: 'Search',
    filter: 'Filter',
    reset: 'Reset',
    failure: 'Failure',
    apply: 'Apply',
    refresh: 'Refresh',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    actions: 'Actions',
    details: 'Details',
    export: 'Export',
    loading: 'Loading',
    noData: 'Nothing to show',
    error: 'Something went wrong',
    retry: 'Try again',
    notPermitted: 'You do not have permission to view this page.',
    invalidUrl: 'Enter a valid URL',
    minCharacters: 'At least {{count}} characters',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    from: 'From',
    to: 'To',
    reason: 'Reason',
    notes: 'Notes',
    amount: 'Amount',
    currency: 'Currency',
    status: 'Status',
    createdAt: 'Created',
    updatedAt: 'Updated',
    signOut: 'Sign out',
    language: 'Language',
    correlationId: 'Correlation ID',
    copied: 'Copied',
    required: 'This field is required',
    page: 'Page',
    of: 'of',
    theme: 'Toggle theme',
    lightMode: 'Light mode',
    darkMode: 'Dark mode',
    profile: 'Profile',
    menu: 'Menu',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    collapseMenu: 'Toggle menu',
    poweredBy: 'Powered by Barah',
    switchLanguage: 'التبديل إلى العربية',
  },
  navGroups: {
    dashboards: 'Dashboards',
    overview: 'Overview',
    money: 'Money movement',
    reports: 'Reports',
    customers: 'Customers and billing',
    catalog: 'Catalogue',
    platform: 'Platform',
    administration: 'Administration',
  },
  nav: {
    dashboard: 'Dashboard',
    payments: 'Payments',
    paymentLinks: 'Payment links',
    customers: 'Customers',
    subscriptions: 'Subscriptions',
    plans: 'Plans',
    billing: 'Billing',
    refunds: 'Refunds',
    disputes: 'Disputes',
    settlements: 'Settlements',
    reconciliation: 'Reconciliation',
    products: 'Products',
    providers: 'Providers',
    webhooks: 'Webhooks',
    events: 'Events',
    reports: 'Reports',
    reportsPayments: 'Payments by period',
    reportsProof: 'Payment proof',
    reportsReconciliation: 'HyperPay reconciliation',
    reportsOther: 'Other reports',
    users: 'Users',
    roles: 'Roles and permissions',
    auditLogs: 'Audit logs',
    systemHealth: 'System health',
    settings: 'Settings',
    investigations: 'Investigations',
  },
  auth: {
    signIn: 'Sign in',
    signInSubtitle: 'Barah Pay administration',
    email: 'Email address',
    password: 'Password',
    mfaCode: 'Verification code',
    mfaRequired: 'Enter the code from your authenticator app',
    signInFailed: 'Sign-in failed',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    mustChangePassword: 'Your password must be changed before you continue.',
    sessionExpired: 'Your session has expired. Please sign in again.',
    welcome: 'Welcome back',
    headline: 'One payment platform for every Barah product',
    lede: 'Payments, subscriptions, refunds, settlement and reconciliation for Meezan 360, BarahBot, CRM and Rikaz — behind a single audited control panel.',
    featurePayments: 'Payments and refunds',
    featureSubscriptions: 'Subscriptions and billing',
    featureReconciliation: 'Settlement reconciliation',
    featureAudit: 'Audited access',
    statScopeValue: 'SAQ A',
    statScopeLabel: 'PCI DSS scope — no card data is stored here',
    statLanguagesValue: 'AR · EN',
    statLanguagesLabel: 'Bilingual portal with full RTL support',
    statMonitoringValue: '24/7',
    statMonitoringLabel: 'Automated provider health monitoring',
  },
  dashboard: {
    todayVolume: "Today's volume",
    todaySuccessful: "Today's successful payments",
    todayFailed: "Today's failed payments",
    todayRefunds: "Today's refunds",
    pendingPayments: 'Pending payments',
    activeSubscriptions: 'Active subscriptions',
    pastDueSubscriptions: 'Past due subscriptions',
    providerHealth: 'Provider health',
    unreconciled: 'Unreconciled transactions',
    nextSettlement: 'Next settlement',
    successRate: 'Success rate',
    openDisputes: 'Open disputes',
    underInvestigation: 'Under investigation',
    openAlerts: 'Open alerts',
    deadLetters: 'Dead-lettered deliveries',
    volumeByDay: 'Payment volume by day',
    successRateByDay: 'Payment success rate',
    paymentsByProduct: 'Payments by product',
    paymentsByMethod: 'Payments by method',
    subscriptionsByProduct: 'Subscriptions by product',
    failureReasons: 'Failed payment reasons',
    settlementTrends: 'Settlement trends',
    greetingMorning: 'Good morning',
    greetingAfternoon: 'Good afternoon',
    greetingEvening: 'Good evening',
    subtitle: "Today's payment operations at a glance",
    lastDays: 'Last {{days}} days',
    total: 'Total',
    other: 'Other',
  },
  payments: {
    title: 'Payments',
    paymentNumber: 'Payment',
    reference: 'Reference',
    product: 'Product',
    tenant: 'Tenant',
    customer: 'Customer',
    method: 'Method',
    channel: 'Channel',
    provider: 'Provider',
    providerTransaction: 'Provider transaction',
    refunded: 'Refunded',
    refundable: 'Refundable',
    timeline: 'Timeline',
    attempts: 'Attempts',
    refunds: 'Refunds',
    webhookEvents: 'Webhook events',
    internalEvents: 'Internal events',
    disputes: 'Disputes',
    settlement: 'Settlement',
    auditHistory: 'Audit history',
    investigation: 'Investigation',
    manualDetail: 'Manual payment detail',
    metadata: 'Metadata',
    cancel: 'Cancel payment',
    syncProvider: 'Sync with provider',
    void: 'Void authorisation',
    requestRefund: 'Request refund',
    openInvestigation: 'Open investigation',
    resolveInvestigation: 'Resolve investigation',
    checkoutUrl: 'Hosted checkout URL',
    amountRange: 'Amount range',
    dateRange: 'Date range',
    underInvestigation: 'Under investigation',
    hasOpenDispute: 'Has an open dispute',
  },
  refunds: {
    title: 'Refunds',
    approve: 'Approve',
    reject: 'Reject',
    process: 'Send to provider',
    approvalQueue: 'Approval queue',
    awaitingApproval: 'Awaiting approval',
    requestedBy: 'Requested by',
    approvedBy: 'Approved by',
    refundType: 'Type',
    approvalRequired: 'Approval required',
    partialAmount: 'Partial amount (leave blank for the full remaining balance)',
    cannotApproveOwn: 'You requested this refund, so a different approver must decide it.',
  },
  subscriptions: {
    searchPlaceholder: 'SUB-… / customer',
    title: 'Subscriptions',
    plan: 'Plan',
    currentPeriod: 'Current period',
    timingAutomatic: 'Automatic (upgrade now, downgrade at period end)',
    timingImmediate: 'Immediate',
    timingAtPeriodEnd: 'At period end',
    nextBilling: 'Next billing',
    gracePeriodEnd: 'Grace period ends',
    grantsAccess: 'Access granted',
    paymentMethod: 'Payment method',
    renewalFailures: 'Renewal failures',
    cycles: 'Billing cycles',
    cancel: 'Cancel',
    cancelAtPeriodEnd: 'Cancel at period end',
    resume: 'Resume',
    suspend: 'Suspend',
    activate: 'Activate',
    changePlan: 'Change plan',
    retryPayment: 'Retry renewal',
    extendTrial: 'Extend trial',
    strategy: 'Renewal strategy',
    fallbackIssued: 'A payment link was issued because the stored card could not be charged.',
  },
  providers: {
    title: 'Providers',
    capabilities: 'Capabilities',
    configuration: 'Configuration',
    merchantAccounts: 'Merchant accounts',
    environment: 'Environment',
    health: 'Health',
    circuitOpen: 'Circuit open',
    closeCircuit: 'Close circuit',
    unconfirmed: 'Unconfirmed with the bank',
    supportsRecurring: 'Recurring',
    supportsTokenization: 'Tokenisation',
    supportsRefund: 'Refund',
    supportsPartialRefund: 'Partial refund',
    supportsVoid: 'Void',
    supports3ds: '3-D Secure',
    credentialsNotShown: 'Credentials are never displayed. Enter a value only to replace it.',
    justification: 'Justification (required for production credential changes)',
  },
  settlements: {
    searchPlaceholder: 'SET-… / bank reference',
    title: 'Settlements',
    gross: 'Gross',
    fees: 'Provider fees',
    vatOnFees: 'VAT on fees',
    net: 'Net settlement',
    variance: 'Variance',
    bankReference: 'Bank reference',
    import: 'Import file',
    fetch: 'Fetch from provider',
    close: 'Close settlement',
    matched: 'Matched',
    unmatched: 'Unmatched',
  },
  reconciliation: {
    title: 'Reconciliation',
    run: 'Run reconciliation',
    outcome: 'Outcome',
    barahPayAmount: 'Barah Pay',
    providerAmount: 'Provider',
    resolve: 'Resolve',
    accept: 'Accept difference',
    escalate: 'Open investigation',
    items: 'Discrepancies',
    matched: 'Matched',
  },
  webhooks: {
    searchPlaceholder: 'PAY-… / provider transaction',
    title: 'Webhooks',
    inbound: 'Inbound from providers',
    outbound: 'Outbound to products',
    signatureValid: 'Signature valid',
    duplicate: 'Duplicate',
    replay: 'Replay',
    retry: 'Retry',
    resolve: 'Mark resolved',
    deadLetter: 'Dead letter',
    payload: 'Payload',
    attempts: 'Attempts',
    targetUrl: 'Target URL',
  },
  reports: {
    title: 'Reports',
    run: 'Run report',
    exportCsv: 'Export CSV',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    totals: 'Totals',
    generatedAt: 'Generated',
    pickReport: 'Choose a report',
    otherTitle: 'Other reports',
    pdfNote: 'PDF files are written in English. Use Excel or CSV for Arabic.',
    period: 'Period',
    systems: 'Systems',
    allSystems: 'All systems',
    allCurrencies: 'Main currency',
    presetToday: 'Today',
    presetYesterday: 'Yesterday',
    presetLast7: 'Last 7 days',
    presetLast30: 'Last 30 days',
    presetThisMonth: 'This month',
    presetLastMonth: 'Last month',
    presetThisYear: 'This year',
    paymentsTitle: 'Payments by period and system',
    paymentsSubtitle: 'What each client system collected, compared with the period before',
    collected: 'Collected',
    payments: 'Payments',
    succeeded: 'Succeeded',
    failed: 'Failed or expired',
    pending: 'Still open',
    refunded: 'Refunded',
    net: 'Net',
    hyperPayFee: 'HyperPay fee',
    netAfterFees: 'Net after fees',
    feeRates: 'credit {{credit}}, mada {{mada}}',
    status: 'Status',
    average: 'Average payment',
    successRate: 'Success rate',
    share: 'Share',
    system: 'System',
    vsPrevious: 'Previous period {{from}} – {{to}}: {{value}}',
    dailyCollected: 'Collected per day',
    bySystem: 'Collected by system',
    byMethod: 'By payment method',
    byStatus: 'By status',
    systemTable: 'Breakdown by system',
    total: 'Total',
    currencyNote: 'Amounts are in {{currency}}. This period also has payments in: {{others}}.',
    proofTitle: 'Payment proof register',
    proofSubtitle: 'The evidence behind every payment: provider confirmation, client notification, settlement and reconciliation',
    searchPlaceholder: 'Payment, provider transaction, reference or tenant',
    evidence: 'Evidence',
    allPayments: 'All payments',
    collectedPayments: 'Collected payments',
    providerConfirmed: 'Provider confirmation',
    confirmed: 'Confirmed',
    notConfirmed: 'Not confirmed',
    clientNotification: 'System notified',
    settlement: 'Settlement',
    notSettled: 'Not settled',
    reconciliation: 'Reconciliation',
    notReconciled: 'Not reconciled',
    resultCode: 'Result code',
    attempts: 'Attempts',
    card: 'Card',
    complete: 'Complete',
    gapsColumn: 'Gaps',
    reconTitle: 'HyperPay reconciliation',
    reconSubtitle: 'Barah Pay records compared with HyperPay, day by day',
    barahPayTotal: 'Barah Pay total',
    providerTotal: 'HyperPay total',
    variance: 'Variance',
    matchedItems: 'Matched',
    discrepancies: 'Discrepancies',
    unresolved: 'Open discrepancies',
    runs: 'Runs',
    failedRuns: 'Failed runs',
    uncoveredTitle: '{{count}} day(s) in this period have not been reconciled',
    uncoveredBody: '{{payments}} collected payment(s) worth {{amount}} have not been compared with HyperPay yet.',
    runForPeriod: 'Reconcile this period',
    runTooLong: 'Reconcile at most 31 days at a time.',
    runDone: 'Reconciliation {{number}} finished: {{matched}} matched, {{mismatches}} discrepancies',
    providerMissing: 'Provider {{code}} is not configured.',
    runHistory: 'Runs covering this period',
    openItems: 'Open discrepancies',
    showAll: 'Include matched and resolved',
    resolveInReconciliation: 'Resolve',
    triggeredBy: 'Run by',
    statuses: {
      Created: 'Created',
      Pending: 'Pending',
      Processing: 'Processing',
      Authorized: 'Authorised',
      Succeeded: 'Succeeded',
      Failed: 'Failed',
      Cancelled: 'Cancelled',
      Expired: 'Expired',
      Refunded: 'Refunded',
      PartiallyRefunded: 'Partially refunded',
      Voided: 'Voided',
      UnderInvestigation: 'Under investigation',
    },
    methods: {
      Unknown: 'Unknown',
      Mada: 'mada',
      Visa: 'Visa',
      Mastercard: 'Mastercard',
      AmericanExpress: 'American Express',
      ApplePay: 'Apple Pay',
      GooglePay: 'Google Pay',
      StcPay: 'STC Pay',
      Urpay: 'urpay',
      Sadad: 'SADAD',
      BankTransfer: 'Bank transfer',
      Cash: 'Cash',
      Cheque: 'Cheque',
    },
    gaps: {
      Any: 'Needs attention',
      ProviderConfirmation: 'No provider confirmation',
      ClientNotification: 'System not notified',
      ReconciliationMismatch: 'Open reconciliation difference',
      Unreconciled: 'Not reconciled',
      Unsettled: 'Not settled',
    },
    notification: {
      Delivered: 'Delivered',
      Failed: 'Failed',
      Retrying: 'Retrying',
      Pending: 'Waiting',
      None: 'Not applicable',
    },
    outcomes: {
      Matched: 'Matched',
      MissingInProvider: 'Missing at HyperPay',
      MissingInBarahPay: 'Missing in Barah Pay',
      AmountMismatch: 'Amount differs',
      StatusMismatch: 'Status differs',
      Duplicate: 'Duplicate charge',
      RefundMismatch: 'Refund differs',
      SettlementMismatch: 'Settlement differs',
    },
    itemStatuses: {
      Open: 'Open',
      UnderInvestigation: 'Under investigation',
      Resolved: 'Resolved',
      Accepted: 'Accepted',
    },
    runStatuses: {
      Running: 'Running',
      Completed: 'Completed',
      CompletedWithDiscrepancies: 'With discrepancies',
      Failed: 'Failed',
    },
  },
  settings: {
    title: 'Settings',
    featureFlags: 'Feature flags',
    errorCatalogue: 'Error catalogue',
    systemSettings: 'System settings',
    category: 'Category',
    value: 'Value',
    sensitive: 'Sensitive',
    updatedBy: 'Updated by',
  },
  users: {
    title: 'Users',
    fullName: 'Full name',
    roles: 'Roles',
    mfa: 'MFA',
    lockedOut: 'Locked out',
    lastLogin: 'Last sign-in',
    createUser: 'Add user',
    resetPassword: 'Reset password',
    apiClients: 'API clients',
    scopes: 'Scopes',
    rotateSecret: 'Rotate secret',
    secretShownOnce: 'Copy this secret now. It cannot be shown again.',
    permissions: 'Permissions',
    createRole: 'Add role',
  },
  paymentLinks: {
    title: 'Payment links',
    create: 'Create link',
    linkId: 'Link',
    url: 'URL',
    amountMode: 'Amount mode',
    fixed: 'Fixed amount',
    customerEntered: 'Customer enters the amount',
    minAmount: 'Minimum amount',
    maxAmount: 'Maximum amount',
    singleUse: 'Single use',
    paymentCount: 'Payments',
    expiresAt: 'Expires',
    expire: 'Expire now',
    copyUrl: 'Copy URL',
    referenceType: 'Reference type',
    referenceId: 'Reference',
  },
  customers: {
    title: 'Customers',
    customerNumber: 'Customer',
    name: 'Name',
    email: 'Email',
    mobile: 'Mobile',
    externalId: 'Product customer id',
    paymentMethods: 'Saved payment methods',
    anonymised: 'Anonymised',
    anonymise: 'Anonymise (PDPL erasure)',
    anonymiseWarning:
      'Personal data is overwritten and cannot be recovered. Financial records are preserved.',
    defaultMethod: 'Default',
    usable: 'Usable',
    expired: 'Expired',
    payments: 'Payments',
    subscriptions: 'Subscriptions',
  },
  plans: {
    title: 'Plans',
    code: 'Code',
    name: 'Name',
    interval: 'Billing interval',
    price: 'Price',
    monthlyEquivalent: 'Monthly equivalent',
    trialDays: 'Trial days',
    graceDays: 'Grace days',
    vatRate: 'VAT rate',
    customPriced: 'Custom priced',
    active: 'Active',
    features: 'Features',
    create: 'Add plan',
    edit: 'Edit plan',
    sortOrder: 'Sort order',
  },
  products: {
    title: 'Products',
    code: 'Code',
    name: 'Name',
    webhookUrl: 'Webhook URL',
    kindBoth: 'Both',
    kindReturn: 'Return',
    kindCancel: 'Cancel',
    webhookSecret: 'Webhook secret',
    generateSecret: 'Generate secret',
    rotateSecret: 'Rotate secret',
    redirectUrls: 'Allowed redirect URLs',
    addRedirectUrl: 'Add redirect URL',
    allowSubPaths: 'Allow sub-paths',
    kind: 'Kind',
    tenants: 'Tenants',
    defaultCurrency: 'Default currency',
    expiryMinutes: 'Payment expiry (minutes)',
    vatRate: 'VAT rate',
    prorateUpgrades: 'Charge upgrades immediately',
    statementDescriptor: 'Statement descriptor',
    supportEmail: 'Support email',
    create: 'Add product',
    edit: 'Edit product',
    active: 'Active',
    secretShownOnce: 'Copy this secret now. It is stored hashed and cannot be shown again.',
  },
  billing: {
    title: 'Billing',
    invoiceNumber: 'Invoice',
    subtotal: 'Subtotal',
    discount: 'Discount',
    vat: 'VAT',
    total: 'Total',
    amountPaid: 'Paid',
    amountDue: 'Due',
    dueDate: 'Due date',
    issuedAt: 'Issued',
    paidAt: 'Paid at',
    period: 'Period',
    unpaidOnly: 'Unpaid only',
    lineItems: 'Line items',
    quantity: 'Quantity',
    unitPrice: 'Unit price',
    lineTotal: 'Line total',
    notAccounting: 'Barah Pay invoices are internal records. Meezan remains the accounting source of truth.',
  },
  events: {
    searchPlaceholder: 'EVT-… / aggregate',
    title: 'Events',
    eventNumber: 'Event',
    eventType: 'Type',
    version: 'Version',
    aggregate: 'Aggregate',
    occurredAt: 'Occurred',
    publishedAt: 'Published',
    nextAttempt: 'Next attempt',
    lastError: 'Last error',
    catalogue: 'Event catalogue',
    payload: 'Payload',
    retry: 'Retry now',
  },
  audit: {
    title: 'Audit logs',
    action: 'Action',
    entityType: 'Entity type',
    entityId: 'Entity',
    actor: 'Actor',
    actorType: 'Actor type',
    ipAddress: 'IP address',
    oldValue: 'Before',
    newValue: 'After',
    succeeded: 'Succeeded',
    occurredAt: 'When',
    appendOnly: 'Audit records are append-only and are never edited or deleted.',
  },
  health: {
    title: 'System health',
    environment: 'Environment',
    version: 'Version',
    database: 'Database',
    reachable: 'Reachable',
    unreachable: 'Unreachable',
    eventTransport: 'Event transport',
    cacheProvider: 'Cache provider',
    queues: 'Queues and backlogs',
    outboxPending: 'Outbox pending',
    outboxFailed: 'Outbox failed',
    outboxDeadLetter: 'Outbox dead-lettered',
    productWebhooksPending: 'Product webhooks pending',
    productWebhooksDeadLetter: 'Product webhooks dead-lettered',
    notificationsQueued: 'Notifications queued',
    webhooksAwaiting: 'Inbound webhooks awaiting processing',
    openAlerts: 'Open alerts',
    adapters: 'Registered provider adapters',
    serverTime: 'Server time',
    alerts: 'Alerts',
    notifications: 'Notifications',
    acknowledge: 'Acknowledge',
    resolveAlert: 'Resolve',
    severity: 'Severity',
    raisedAt: 'Raised',
    occurrences: 'Occurrences',
  },
  investigations: {
    title: 'Investigations',
    reason: 'Reason',
    resolution: 'Resolution',
    openedBy: 'Opened by',
    openedAt: 'Opened',
    resolvedBy: 'Resolved by',
    statusAtOpen: 'Status when opened',
    providerReported: 'Provider reported',
    lateSuccessNotice:
      'A provider reported success after the payment had expired. Money is never written off silently — resolve the investigation to record the outcome.',
    resolve: 'Resolve',
    confirmProviderSuccess: 'Confirm the provider succeeded',
    confirmProviderFailure: 'Confirm the provider did not succeed',
    refundIssued: 'Refunded to the customer',
    noActionRequired: 'No action required',
  },
  checkout: {
    payNow: 'Pay now',
    payAmount: 'Pay {{amount}}',
    amountToPay: 'Amount to pay',
    enterAmount: 'Enter the amount to pay',
    choosePaymentMethod: 'Choose a payment method',
    securePayment: 'Secure payment',
    securedBy: 'Payments are processed securely by Barah Pay.',
    poweredBy: 'Powered by Barah Pay',
    expiresAt: 'This payment request expires',
    reference: 'Reference',
    paymentSuccessful: 'Payment successful',
    paymentFailed: 'Payment not completed',
    paymentPending: 'Waiting for payment',
    paymentExpired: 'This payment request has expired',
    paymentCancelled: 'Payment cancelled',
    underReview: 'Your payment is being reviewed',
    returnToMerchant: 'Return to {{merchant}}',
    returningShortly: 'You will be taken back to {{merchant}} automatically.',
    completedAt: 'Completed',
    redirecting: 'Taking you to your bank securely',
    preparingPayment: 'Preparing your payment',
    processingPayment: 'Processing payment',
    verifyingPayment: 'Verifying payment',
    doNotClose: 'Please do not close or refresh this window.',
    stillVerifying: 'This is taking longer than usual. We are still checking with your bank.',

    /*
     * Shown once the page has waited as long as it is going to. It says "we cannot confirm", never
     * "it failed" and never "it is pending": the payment may well have gone through, and a payer told
     * either of the other two acts on it — by paying again, or by walking away from money that left
     * their account.
     */
    notConfirmed: 'We could not confirm your payment yet',
    doNotPayAgain: 'Please do not pay again. If your bank took the money, this payment is with us and paying twice would be charged twice.',
    weWillFollowUp: 'We are checking with your bank. Quote the payment number below if you contact support.',

    checkAgain: 'Check again',
    connectionLost: 'We could not reach Barah Pay. Check your connection and try again.',
    notAvailable: 'This payment page is no longer available',
    tryAgain: 'Try again',
    contactSupport: 'Contact support',
    minAmount: 'Minimum {{amount}}',
    maxAmount: 'Maximum {{amount}}',
    cardDetailsNotice: 'Card details are entered on your bank’s page, never on Barah Pay.',
    cardDetailsNoticeEmbedded: 'Card details go straight to your bank’s payment provider. Barah Pay never sees them.',
    widgetUnavailable: 'The secure payment form could not be loaded. Please try again, or contact the merchant if it keeps happening.',
    noMethodsAvailable: 'No payment method is available for this payment right now. Please try again shortly, or contact the merchant.',
    providerTemporarilyUnavailable: 'Payment is briefly unavailable. This page checks again by itself every few seconds, so there is no need to leave it.',
    previousAttemptFailed: 'Your previous attempt did not go through and no money was taken. You can try again below, with another card if you prefer.',
    noMoneyTaken: 'No money was taken. This payment link still works, so you can try again — with another card if you prefer.',
    threeDSecure: '3-D Secure',
  },

  /*
   * Field labels and column headings shared across the portal.
   *
   * These were written straight into the components, which is why a complete resource file still gave
   * a half-English screen: a string that was never a key cannot be a missing one, so key-coverage
   * checks reported nothing wrong. They live in one group rather than per page because most of them —
   * "Merchant id", "Timeout", "Provider reference" — appear on several screens and must read the same
   * on each.
   */
  fields: {
    adapter: 'Adapter',
    addEvidence: 'Add evidence',
    adapterSettings: 'Adapter settings',
    adapterSettingsJson: 'Adapter settings (JSON)',
    adjustments: 'Adjustments',
    beginReview: 'Begin review',
    allowedCallbackIps: 'Allowed callback IPs',
    allowedIps: 'Allowed IPs',
    allowedOrigins: 'Allowed origins',
    apiKey: 'API key',
    approval: 'Approval',
    attachment: 'Attachment',
    attachmentReference: 'Attachment reference',
    bankValueDate: 'Bank value date',
    barahPayTransactions: 'Barah Pay transactions',
    baseUrl: 'Base URL',
    brand: 'Brand',
    breakerDuration: 'Breaker duration',
    breakerDurationSeconds: 'Breaker duration (s)',
    breakerThreshold: 'Breaker threshold',
    calculatedNet: 'Calculated net',
    callbackIps: 'Callback IPs',
    cancelled: 'Cancelled',
    channel: 'Channel',
    chargebacks: 'Chargebacks',
    clientId: 'Client id',
    clientSecret: 'Client secret',
    code: 'Code',
    commaSeparated: 'Comma separated',
    countSuffix: '× count',
    date: 'Date',
    decidedBy: 'Decided by',
    delivered: 'Delivered',
    disputeFee: 'Dispute fee',
    duration: 'Duration',
    english: 'English',
    enteredBy: 'Entered by',
    enteredManually: 'Manual',
    evidence: 'Evidence',
    evidenceDue: 'Evidence due',
    evidenceDueSoon: 'Evidence due soon',
    failuresOnly: 'Failures only',
    evidenceSubmitted: 'Evidence submitted',
    expires: 'Expires',
    expiry: 'Expiry',
    externalId: 'External id',
    failed: 'Failed',
    headers: 'Headers',
    invoice: 'Invoice',
    invoiceOrderExample: 'Invoice, Order, …',
    justification: 'Justification',
    key: 'Key',
    keyVersion: 'Key version',
    lastResponse: 'Last response',
    lastSuccess: 'Last success',
    lastUsed: 'Last used',
    latency: 'Latency',
    logoUrl: 'Logo URL',
    merchantId: 'Merchant id',
    noAdapter: 'No adapter',
    oneFeaturePerLine: 'One feature per line',
    operation: 'Operation',
    openOnly: 'Open only',
    optional: 'Optional',
    outcome: 'Outcome',
    processed: 'Processed',
    providerReference: 'Provider reference',
    providerTransactions: 'Provider transactions',
    rateLimit: 'Rate limit',
    rateLimitPerMinute: 'Rate limit / minute',
    received: 'Received',
    recipient: 'Recipient',
    reconciled: 'Reconciled',
    recordDispute: 'Record a dispute',
    refundWindowDays: 'Refund window (days)',
    refunds: 'Refunds',
    replayedAt: 'Replayed at',
    replayedBy: 'Replayed by',
    resolved: 'Resolved',
    response: 'Response',
    retries: 'Retries',
    reversalRefund: 'Reversal refund',
    secretRotated: 'Secret rotated',
    secretsRotated: 'Secrets rotated',
    sent: 'Sent',
    settlementAccountReference: 'Settlement account reference',
    settlementDate: 'Settlement date',
    settlementImpact: 'Settlement impact',
    signingKey: 'Signing key',
    sourceFile: 'Source file',
    sourceIp: 'Source IP',
    start: 'Start',
    started: 'Started',
    subject: 'Subject',
    submitToProviderNow: 'Submit to the provider now',
    succeeded: 'Succeeded',
    successful: 'Successful',
    tenant: 'Tenant',
    terminalId: 'Terminal id',
    timeout: 'Timeout',
    timeoutSeconds: 'Timeout (s)',
    timestampValid: 'Timestamp valid',
    timing: 'Timing',
    toggleNavigation: 'Toggle navigation',
    tokenLifetimeSeconds: 'Token lifetime (s)',
    tokenUrl: 'Token URL',
    transactions: 'Transactions',
    trial: 'Trial',
    triggeredBy: 'Triggered by',
    type: 'Type',
    valueDate: 'Value date',
    vatNumber: 'VAT number',
    webhookSecret: 'Webhook secret',
    webhookTolerance: 'Webhook tolerance',
    webhookToleranceSeconds: 'Webhook tolerance (s)',
    window: 'Window',

    // Provider capability columns.
    hosted: 'Hosted',
    inquiry: 'Inquiry',
    lastError: 'Last error',
    links: 'Links',
    mitAllowed: 'MIT allowed',
    refundWindow: 'Refund window',
    settlementAccount: 'Settlement account',
    secret: 'Secret',
    settlementApi: 'Settlement API',
    supported: 'Supported',
    systemRole: 'System',
  },

  /** Explanatory notes shown above a form or table. */
  notes: {
    featureFlagTakesEffect: 'A flag change takes effect on the next request; no restart is needed.',
    manualDisputeNeedsApprover: 'Manual dispute entry requires a second approver before it affects settlement figures.',
    disputeDoesNotAlterPayment:
      'A dispute records the bank’s claim. The underlying payment is never altered — a reversal is booked as a refund.',
    rolePermissionsAreServerSide:
      'Permissions are enforced by the API on every request. Changing a role takes effect immediately, without waiting for the user’s token to expire.',
    adapterSettingsShape: 'A flat JSON object; every value must be a string.',
  },

  /*
   * Text for rows the platform seeds itself: the nine built-in roles and the ten feature flags.
   *
   * These arrive from the API as English prose written into the database by the seeder, so no
   * resource file could reach them and the Arabic portal showed English sentences in both tables.
   * Keying off the stable role name and flag key translates them without a schema change, and
   * anything unrecognised — a role an operator created, with a description they wrote — keeps the
   * server's text rather than being overwritten by a guess.
   */
  /*
   * The thirty system settings and the categories they group under, both seeded into the database
   * as English prose. Dots in a setting key are a nesting separator to i18next, so the key is
   * stored here with underscores and the lookup swaps them — see translateSettingDescription.
   */
  settingDescriptions: {
    alerts_minimum_sample_size: 'Minimum payments before the failure rate is evaluated.',
    alerts_payment_failure_rate_percent: 'Failure-rate percentage that raises an alert.',
    disputes_evidence_alert_days: 'Days before an evidence deadline that finance is alerted.',
    outbox_max_attempts: 'Attempts before an event is dead-lettered.',
    outbox_retry_schedule_seconds: 'Outbox publish retry ladder in seconds.',
    idempotency_retention_hours: 'How long an idempotency key is honoured (minimum 24).',
    payments_default_currency: 'Currency used when a caller supplies none.',
    payments_expiry_minutes: 'Minutes before an unpaid payment expires.',
    payments_max_amount: 'Largest single payment the platform will accept.',
    payments_metadata_max_bytes: 'Maximum serialised size of product metadata.',
    payments_supported_currencies: 'Comma-separated ISO 4217 codes the platform accepts.',
    payments_supported_methods: 'Payment methods offered platform-wide.',
    providers_status_sync_after_minutes: 'Minutes in Processing before the provider is re-queried.',
    providers_timeout_seconds: 'Default provider HTTP timeout.',
    refunds_approval_currency: 'Currency the approval threshold is expressed in.',
    refunds_approval_threshold: 'Refunds at or above this amount need a second approver.',
    retention_audit_days: 'Days audit records are kept (seven years).',
    retention_notifications_days: 'Days notification records are kept.',
    retention_payments_days: 'Payment records are retained, never purged, while obligations apply.',
    retention_webhooks_days: 'Days webhook payloads are kept before cleanup.',
    subscriptions_grace_days: 'Days of access after a failed renewal.',
    subscriptions_renewal_lookahead_minutes: 'How far ahead the renewal job looks for due subscriptions.',
    subscriptions_retry_schedule_days: 'Renewal retry ladder in days after the renewal date.',
    subscriptions_trial_ending_notice_days: 'Days before trial end that the customer is notified.',
    tax_enabled: 'Whether VAT is applied to billing invoices.',
    tax_vat_rate: 'Default VAT rate applied to billing invoices.',
    urls_admin_base: 'Admin portal host.',
    urls_checkout_base: 'Public hosted checkout host.',
    webhooks_max_attempts: 'Attempts before a product webhook is dead-lettered.',
    webhooks_retry_schedule_minutes: 'Outbound product webhook retry ladder in minutes.',
  },

  settingCategories: {
    Alerting: 'Alerting',
    Disputes: 'Disputes',
    Events: 'Events',
    Payments: 'Payments',
    Providers: 'Providers',
    Refunds: 'Refunds',
    Retention: 'Retention',
    Subscriptions: 'Subscriptions',
    Tax: 'Tax',
    Urls: 'URLs',
    Webhooks: 'Webhooks',
  },

  roleDescriptions: {
    'Super Admin': 'Full access to every part of the platform.',
    'Finance Admin': 'Finance operations including refunds, settlements and reconciliation.',
    'Finance Viewer': 'Read-only access to financial data and reports.',
    'Payment Operations': 'Day-to-day payment operations and investigations.',
    'Refund Approver': 'Approves refunds above the configured threshold.',
    'Reconciliation Officer': 'Runs and resolves reconciliation.',
    'Technical Support': 'Diagnoses payment and webhook issues.',
    Developer: 'Manages products, API clients and integration configuration.',
    Auditor: 'Read-only access to audit logs and financial records.',
  },

  flagDescriptions: {
    EnableRecurringPayments: 'Charge stored tokens for subscription renewals.',
    EnableApplePay: 'Offer Apple Pay in hosted checkout.',
    EnablePartialRefund: 'Allow refunds for less than the full payment amount.',
    EnableProviderRouting: 'Evaluate provider routing rules instead of using the default provider.',
    EnableMakerChecker: 'Require a second approver for high-risk operations.',
    EnableNewCheckout: 'Serve the current hosted checkout experience.',
    EnableManualPayments: 'Allow operators to record bank transfer, SADAD, cash and cheque payments.',
    EnableProductWebhooks: 'Deliver signed webhooks to products in addition to the event bus.',
    EnableSubscriptionProration: 'Charge a prorated difference on immediate plan upgrades.',
    EnableEmbeddedCheckout: 'Let a provider render its payment form on the checkout page instead of redirecting.',
  },

  /*
   * Text for every code in the Barah Pay error catalogue (spec section 52), plus the three
   * client-side conditions the API never reports because the request never reached it.
   *
   * The API returns a stable code and an English detail line; it does not translate. Rendering the
   * detail put an English sentence under an Arabic heading on every failure, so the portal keys off
   * the code instead and the server text becomes a diagnostic rather than the message.
   */
  errors: {
    PAYMENT_DECLINED: 'The payment was declined. Try another card or contact your bank.',
    PAYMENT_EXPIRED: 'This payment request has expired.',
    PAYMENT_NOT_FOUND: 'This payment could not be found.',
    PAYMENT_ALREADY_COMPLETED: 'This payment has already been completed.',
    PAYMENT_NOT_CANCELLABLE: 'This payment can no longer be cancelled.',
    PAYMENT_AMOUNT_INVALID: 'The payment amount is not valid.',
    PAYMENT_UNDER_INVESTIGATION: 'This payment is under investigation and cannot be changed.',
    INSUFFICIENT_FUNDS: 'There were not enough funds to complete this payment.',
    INVALID_PAYMENT_METHOD: 'That payment method is not available for this payment.',
    INVALID_STATE_TRANSITION: 'This action is not allowed while the payment is in its current status.',
    REFERENCE_LIMIT_EXCEEDED: 'This reference has been used too many times.',
    AUTHENTICATION_FAILED_3DS: 'Card verification was not completed.',
    PROVIDER_UNAVAILABLE: 'The payment provider is temporarily unavailable. Try again shortly.',
    PROVIDER_TIMEOUT: 'The payment provider did not respond in time. Try again shortly.',
    PROVIDER_ERROR: 'The payment provider returned an error. Try again shortly.',
    PROVIDER_NOT_CONFIGURED: 'This provider has no credentials configured for the current environment.',
    CAPABILITY_NOT_SUPPORTED: 'The provider does not support this operation.',
    REFUND_NOT_ALLOWED: 'This payment cannot be refunded.',
    REFUND_AMOUNT_EXCEEDS_PAYMENT: 'The refund amount is greater than the amount remaining on the payment.',
    REFUND_NOT_FOUND: 'This refund could not be found.',
    REFUND_APPROVAL_REQUIRED: 'This refund needs a second approver before it can be processed.',
    REFUND_ALREADY_PROCESSED: 'This refund has already been processed.',
    VOID_NOT_ALLOWED: 'This authorisation cannot be voided.',
    SUBSCRIPTION_NOT_FOUND: 'This subscription could not be found.',
    SUBSCRIPTION_PAST_DUE: 'The subscription payment is overdue.',
    SUBSCRIPTION_NOT_ACTIVE: 'This subscription is not active.',
    SUBSCRIPTION_ALREADY_CANCELLED: 'This subscription has already been cancelled.',
    PLAN_NOT_FOUND: 'This plan could not be found.',
    PLAN_NOT_ACTIVE: 'This plan is not active.',
    PAYMENT_METHOD_TOKEN_MISSING: 'No saved payment method is available for this subscription.',
    RECURRING_NOT_SUPPORTED: 'The provider does not support recurring payments.',
    PAYMENT_LINK_NOT_FOUND: 'This payment link could not be found.',
    PAYMENT_LINK_EXPIRED: 'This payment link has expired.',
    PAYMENT_LINK_ALREADY_USED: 'This payment link has already been used.',
    DUPLICATE_REQUEST: 'This request has already been submitted.',
    IDEMPOTENCY_KEY_CONFLICT: 'This idempotency key was already used with a different request.',
    IDEMPOTENCY_REQUEST_IN_PROGRESS: 'An identical request is still being processed. Wait a moment and check the result.',
    VALIDATION_FAILED: 'The request is not valid. Check the highlighted fields.',
    CONCURRENCY_CONFLICT: 'Someone else changed this record. Refresh and try again.',
    RATE_LIMIT_EXCEEDED: 'Too many requests. Wait a moment and try again.',
    METADATA_INVALID: 'The metadata is not valid.',
    CURRENCY_NOT_SUPPORTED: 'This currency is not supported.',
    REDIRECT_URL_NOT_ALLOWED: 'That return URL is not registered for this product.',
    UNAUTHORIZED: 'Your session has ended. Sign in again.',
    FORBIDDEN: 'This operation is not permitted.',
    SCOPE_MISSING: 'This API client does not have the scope this operation needs.',
    PERMISSION_MISSING: 'You do not have the permission this operation needs.',
    INVALID_CREDENTIALS: 'The email address or password is incorrect.',
    MFA_REQUIRED: 'Multi-factor authentication is required.',
    MFA_INVALID: 'That verification code is not correct.',
    TOKEN_INVALID: 'That token is not valid or has expired.',
    PRODUCT_NOT_FOUND: 'This product could not be found.',
    PRODUCT_INACTIVE: 'This product is not active.',
    CUSTOMER_NOT_FOUND: 'This customer could not be found.',
    TENANT_NOT_FOUND: 'This tenant could not be found.',
    SETTING_NOT_FOUND: 'This setting could not be found.',
    FEATURE_DISABLED: 'This feature is switched off.',
    APPROVAL_REQUIRED: 'This change needs a second approver.',
    APPROVAL_NOT_ALLOWED: 'You cannot approve this request.',
    SELF_APPROVAL_NOT_ALLOWED: 'You cannot approve a request you raised yourself.',
    WEBHOOK_SIGNATURE_INVALID: 'The callback signature could not be verified.',
    WEBHOOK_TIMESTAMP_INVALID: 'The callback timestamp is outside the accepted window.',
    WEBHOOK_REPLAY_DETECTED: 'This callback has already been received.',
    WEBHOOK_NOT_FOUND: 'This webhook could not be found.',
    SETTLEMENT_NOT_FOUND: 'This settlement could not be found.',
    SETTLEMENT_FILE_INVALID: 'The settlement file could not be read.',
    RECONCILIATION_NOT_FOUND: 'This reconciliation run could not be found.',
    DISPUTE_NOT_FOUND: 'This dispute could not be found.',
    DISPUTE_ALREADY_RESOLVED: 'This dispute has already been resolved.',
    INTERNAL_ERROR: 'Something went wrong on our side. Try again shortly.',
    NETWORK_ERROR: 'Could not reach Barah Pay. Check your connection and try again.',
    REQUEST_TIMEOUT: 'The request took too long. Try again.',
    UNKNOWN: 'Something went wrong. Try again.',
  },
};

const ar: typeof en = {
  common: {
    appName: 'براح باي',
    portalTitle: 'براح باي — لوحة الإدارة',
    search: 'بحث',
    filter: 'تصفية',
    reset: 'إعادة تعيين',
    failure: 'الخطأ',
    apply: 'تطبيق',
    refresh: 'تحديث',
    save: 'حفظ',
    cancel: 'إلغاء',
    close: 'إغلاق',
    confirm: 'تأكيد',
    actions: 'الإجراءات',
    details: 'التفاصيل',
    export: 'تصدير',
    loading: 'جارٍ التحميل',
    noData: 'لا توجد بيانات',
    error: 'حدث خطأ',
    retry: 'إعادة المحاولة',
    notPermitted: 'ليس لديك صلاحية لعرض هذه الصفحة.',
    invalidUrl: 'أدخل رابطًا صحيحًا',
    minCharacters: 'ما لا يقل عن {{count}} حرفًا',
    yes: 'نعم',
    no: 'لا',
    all: 'الكل',
    from: 'من',
    to: 'إلى',
    reason: 'السبب',
    notes: 'ملاحظات',
    amount: 'المبلغ',
    currency: 'العملة',
    status: 'الحالة',
    createdAt: 'تاريخ الإنشاء',
    updatedAt: 'آخر تحديث',
    signOut: 'تسجيل الخروج',
    language: 'اللغة',
    correlationId: 'معرف التتبع',
    copied: 'تم النسخ',
    required: 'هذا الحقل مطلوب',
    page: 'صفحة',
    of: 'من',
    theme: 'تبديل المظهر',
    lightMode: 'الوضع الفاتح',
    darkMode: 'الوضع الداكن',
    profile: 'الملف الشخصي',
    menu: 'القائمة',
    openMenu: 'فتح القائمة',
    closeMenu: 'إغلاق القائمة',
    collapseMenu: 'طيّ القائمة',
    poweredBy: 'مدعوم من بـراح',
    switchLanguage: 'Switch to English',
  },
  navGroups: {
    dashboards: 'لوحات المعلومات',
    overview: 'نظرة عامة',
    money: 'حركة الأموال',
    reports: 'التقارير',
    customers: 'العملاء والفواتير',
    catalog: 'الكتالوج',
    platform: 'المنصة',
    administration: 'الإدارة',
  },
  nav: {
    dashboard: 'لوحة المعلومات',
    payments: 'المدفوعات',
    paymentLinks: 'روابط الدفع',
    customers: 'العملاء',
    subscriptions: 'الاشتراكات',
    plans: 'الخطط',
    billing: 'الفواتير',
    refunds: 'المبالغ المستردة',
    disputes: 'الاعتراضات',
    settlements: 'التسويات',
    reconciliation: 'المطابقة',
    products: 'المنتجات',
    providers: 'مزودو الدفع',
    webhooks: 'الويب هوك',
    events: 'الأحداث',
    reports: 'التقارير',
    reportsPayments: 'المدفوعات حسب الفترة',
    reportsProof: 'إثبات المدفوعات',
    reportsReconciliation: 'مطابقة HyperPay',
    reportsOther: 'تقارير أخرى',
    users: 'المستخدمون',
    roles: 'الأدوار والصلاحيات',
    auditLogs: 'سجل التدقيق',
    systemHealth: 'حالة النظام',
    settings: 'الإعدادات',
    investigations: 'التحقيقات',
  },
  auth: {
    signIn: 'تسجيل الدخول',
    signInSubtitle: 'إدارة براح باي',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    mfaCode: 'رمز التحقق',
    mfaRequired: 'أدخل الرمز من تطبيق المصادقة',
    signInFailed: 'فشل تسجيل الدخول',
    changePassword: 'تغيير كلمة المرور',
    currentPassword: 'كلمة المرور الحالية',
    newPassword: 'كلمة المرور الجديدة',
    mustChangePassword: 'يجب تغيير كلمة المرور قبل المتابعة.',
    sessionExpired: 'انتهت الجلسة. الرجاء تسجيل الدخول مرة أخرى.',
    welcome: 'مرحباً بعودتك',
    headline: 'منصة مدفوعات واحدة لجميع منتجات براح',
    lede: 'المدفوعات والاشتراكات والمبالغ المستردة والتسويات والمطابقة لميزان 360 وبراح بوت وإدارة العلاقات وركاز — من لوحة تحكم واحدة موثّقة بالكامل.',
    featurePayments: 'المدفوعات والاسترداد',
    featureSubscriptions: 'الاشتراكات والفواتير',
    featureReconciliation: 'مطابقة التسويات',
    featureAudit: 'وصول موثّق بالسجلات',
    statScopeValue: 'SAQ A',
    statScopeLabel: 'نطاق PCI DSS — لا تُخزَّن بيانات البطاقات هنا',
    statLanguagesValue: 'ع · EN',
    statLanguagesLabel: 'لوحة ثنائية اللغة بدعم كامل للاتجاه من اليمين',
    statMonitoringValue: '24/7',
    statMonitoringLabel: 'مراقبة آلية لحالة مزودي الدفع',
  },
  dashboard: {
    todayVolume: 'إجمالي اليوم',
    todaySuccessful: 'المدفوعات الناجحة اليوم',
    todayFailed: 'المدفوعات الفاشلة اليوم',
    todayRefunds: 'المبالغ المستردة اليوم',
    pendingPayments: 'مدفوعات معلقة',
    activeSubscriptions: 'اشتراكات نشطة',
    pastDueSubscriptions: 'اشتراكات متأخرة',
    providerHealth: 'حالة مزودي الدفع',
    unreconciled: 'عمليات غير مطابقة',
    nextSettlement: 'التسوية القادمة',
    successRate: 'نسبة النجاح',
    openDisputes: 'اعتراضات مفتوحة',
    underInvestigation: 'تحت التحقيق',
    openAlerts: 'تنبيهات مفتوحة',
    deadLetters: 'إشعارات فاشلة نهائياً',
    volumeByDay: 'حجم المدفوعات باليوم',
    successRateByDay: 'نسبة نجاح المدفوعات',
    paymentsByProduct: 'المدفوعات بحسب المنتج',
    paymentsByMethod: 'المدفوعات بحسب الطريقة',
    subscriptionsByProduct: 'الاشتراكات بحسب المنتج',
    failureReasons: 'أسباب فشل المدفوعات',
    settlementTrends: 'اتجاهات التسوية',
    greetingMorning: 'صباح الخير',
    greetingAfternoon: 'طاب يومك',
    greetingEvening: 'مساء الخير',
    subtitle: 'نظرة سريعة على عمليات الدفع اليوم',
    lastDays: 'آخر {{days}} يوم',
    total: 'الإجمالي',
    other: 'أخرى',
  },
  payments: {
    title: 'المدفوعات',
    paymentNumber: 'رقم الدفع',
    reference: 'المرجع',
    product: 'المنتج',
    tenant: 'المستأجر',
    customer: 'العميل',
    method: 'طريقة الدفع',
    channel: 'القناة',
    provider: 'مزود الدفع',
    providerTransaction: 'معرف عملية المزود',
    refunded: 'المبلغ المسترد',
    refundable: 'القابل للاسترداد',
    timeline: 'التسلسل الزمني',
    attempts: 'المحاولات',
    refunds: 'المبالغ المستردة',
    webhookEvents: 'أحداث الويب هوك',
    internalEvents: 'الأحداث الداخلية',
    disputes: 'الاعتراضات',
    settlement: 'التسوية',
    auditHistory: 'سجل التدقيق',
    investigation: 'التحقيق',
    manualDetail: 'تفاصيل الدفع اليدوي',
    metadata: 'بيانات إضافية',
    cancel: 'إلغاء الدفع',
    syncProvider: 'مزامنة مع المزود',
    void: 'إلغاء التفويض',
    requestRefund: 'طلب استرداد',
    openInvestigation: 'بدء تحقيق',
    resolveInvestigation: 'إغلاق التحقيق',
    checkoutUrl: 'رابط صفحة الدفع',
    amountRange: 'نطاق المبلغ',
    dateRange: 'نطاق التاريخ',
    underInvestigation: 'تحت التحقيق',
    hasOpenDispute: 'يوجد اعتراض مفتوح',
  },
  refunds: {
    title: 'المبالغ المستردة',
    approve: 'اعتماد',
    reject: 'رفض',
    process: 'إرسال للمزود',
    approvalQueue: 'قائمة الاعتماد',
    awaitingApproval: 'بانتظار الاعتماد',
    requestedBy: 'مقدم الطلب',
    approvedBy: 'المعتمد',
    refundType: 'النوع',
    approvalRequired: 'يتطلب اعتماداً',
    partialAmount: 'مبلغ جزئي (اتركه فارغاً لاسترداد كامل المتبقي)',
    cannotApproveOwn: 'أنت مقدم طلب الاسترداد، لذا يجب أن يعتمده شخص آخر',
  },
  subscriptions: {
    searchPlaceholder: 'SUB-… / العميل',
    title: 'الاشتراكات',
    plan: 'الخطة',
    currentPeriod: 'الفترة الحالية',
    timingAutomatic: 'تلقائي (ترقية فورية وتخفيض في نهاية الفترة)',
    timingImmediate: 'فوري',
    timingAtPeriodEnd: 'في نهاية الفترة',
    nextBilling: 'الفاتورة القادمة',
    gracePeriodEnd: 'انتهاء فترة السماح',
    grantsAccess: 'الوصول مفعّل',
    paymentMethod: 'طريقة الدفع',
    renewalFailures: 'محاولات تجديد فاشلة',
    cycles: 'دورات الفوترة',
    cancel: 'إلغاء',
    cancelAtPeriodEnd: 'الإلغاء في نهاية الفترة',
    resume: 'استئناف',
    suspend: 'تعليق',
    activate: 'تنشيط',
    changePlan: 'تغيير الخطة',
    retryPayment: 'إعادة محاولة التجديد',
    extendTrial: 'تمديد الفترة التجريبية',
    strategy: 'طريقة التجديد',
    fallbackIssued: 'تم إصدار رابط دفع لعدم إمكانية خصم البطاقة المحفوظة.',
  },
  providers: {
    title: 'مزودو الدفع',
    capabilities: 'القدرات',
    configuration: 'الإعدادات',
    merchantAccounts: 'حسابات التاجر',
    environment: 'البيئة',
    health: 'الحالة',
    circuitOpen: 'الدائرة مفتوحة',
    closeCircuit: 'إغلاق الدائرة',
    unconfirmed: 'غير مؤكد من البنك',
    supportsRecurring: 'الدفع المتكرر',
    supportsTokenization: 'الترميز',
    supportsRefund: 'الاسترداد',
    supportsPartialRefund: 'استرداد جزئي',
    supportsVoid: 'إلغاء التفويض',
    supports3ds: 'التحقق الثلاثي',
    credentialsNotShown: 'لا تُعرض بيانات الاعتماد. أدخل قيمة فقط لاستبدالها.',
    justification: 'المبرر (مطلوب لتغيير بيانات الإنتاج)',
  },
  settlements: {
    searchPlaceholder: 'SET-… / المرجع البنكي',
    title: 'التسويات',
    gross: 'الإجمالي',
    fees: 'عمولات المزود',
    vatOnFees: 'ضريبة العمولات',
    net: 'صافي التسوية',
    variance: 'الفرق',
    bankReference: 'مرجع البنك',
    import: 'استيراد ملف',
    fetch: 'جلب من المزود',
    close: 'إغلاق التسوية',
    matched: 'مطابق',
    unmatched: 'غير مطابق',
  },
  reconciliation: {
    title: 'المطابقة',
    run: 'تشغيل المطابقة',
    outcome: 'النتيجة',
    barahPayAmount: 'براح باي',
    providerAmount: 'المزود',
    resolve: 'معالجة',
    accept: 'قبول الفرق',
    escalate: 'بدء تحقيق',
    items: 'الفروقات',
    matched: 'مطابق',
  },
  webhooks: {
    searchPlaceholder: 'PAY-… / معاملة المزوّد',
    title: 'الويب هوك',
    inbound: 'الوارد من المزودين',
    outbound: 'الصادر إلى المنتجات',
    signatureValid: 'التوقيع صحيح',
    duplicate: 'مكرر',
    replay: 'إعادة المعالجة',
    retry: 'إعادة المحاولة',
    resolve: 'تحديد كمعالج',
    deadLetter: 'فاشل نهائياً',
    payload: 'المحتوى',
    attempts: 'المحاولات',
    targetUrl: 'الرابط المستهدف',
  },
  reports: {
    title: 'التقارير',
    run: 'تشغيل التقرير',
    exportCsv: 'تصدير CSV',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    totals: 'الإجماليات',
    generatedAt: 'تاريخ الإصدار',
    pickReport: 'اختر التقرير',
    otherTitle: 'تقارير أخرى',
    pdfNote: 'ملفات PDF تُكتب بالإنجليزية. استخدم Excel أو CSV للعربية.',
    period: 'الفترة',
    systems: 'الأنظمة',
    allSystems: 'كل الأنظمة',
    allCurrencies: 'العملة الرئيسية',
    presetToday: 'اليوم',
    presetYesterday: 'أمس',
    presetLast7: 'آخر 7 أيام',
    presetLast30: 'آخر 30 يوم',
    presetThisMonth: 'هذا الشهر',
    presetLastMonth: 'الشهر الماضي',
    presetThisYear: 'هذه السنة',
    paymentsTitle: 'تقرير المدفوعات حسب الفترة والنظام',
    paymentsSubtitle: 'ما حصّله كل نظام خلال الفترة مقارنةً بالفترة السابقة',
    collected: 'المحصّل',
    payments: 'عدد المدفوعات',
    succeeded: 'ناجحة',
    failed: 'فاشلة أو منتهية',
    pending: 'لم تكتمل بعد',
    refunded: 'المسترد',
    net: 'الصافي',
    hyperPayFee: 'عمولة HyperPay',
    netAfterFees: 'الصافي بعد العمولة',
    feeRates: 'ائتمانية {{credit}}، مدى {{mada}}',
    status: 'الحالة',
    average: 'متوسط الدفعة',
    successRate: 'نسبة النجاح',
    share: 'الحصة',
    system: 'النظام',
    vsPrevious: 'الفترة السابقة {{from}} – {{to}}: {{value}}',
    dailyCollected: 'التحصيل اليومي',
    bySystem: 'التحصيل حسب النظام',
    byMethod: 'حسب طريقة الدفع',
    byStatus: 'حسب الحالة',
    systemTable: 'التفصيل حسب النظام',
    total: 'الإجمالي',
    currencyNote: 'المبالغ بعملة {{currency}}. توجد في هذه الفترة مدفوعات بعملات أخرى: {{others}}.',
    proofTitle: 'سجل إثبات المدفوعات',
    proofSubtitle: 'الأدلة وراء كل دفعة: تأكيد المزود، وإشعار النظام، والتسوية، والمطابقة',
    searchPlaceholder: 'رقم الدفعة أو عملية المزود أو المرجع أو المستأجر',
    evidence: 'الإثبات',
    allPayments: 'كل المدفوعات',
    collectedPayments: 'مدفوعات محصّلة',
    providerConfirmed: 'تأكيد المزود',
    confirmed: 'مؤكد',
    notConfirmed: 'غير مؤكد',
    clientNotification: 'إشعار النظام',
    settlement: 'التسوية',
    notSettled: 'لم تُسوَّ',
    reconciliation: 'المطابقة',
    notReconciled: 'لم تُطابق',
    resultCode: 'كود النتيجة',
    attempts: 'المحاولات',
    card: 'البطاقة',
    complete: 'مكتمل',
    gapsColumn: 'الفجوات',
    reconTitle: 'مطابقة HyperPay',
    reconSubtitle: 'مقارنة سجلات براح باي مع سجلات HyperPay يوماً بيوم',
    barahPayTotal: 'إجمالي براح باي',
    providerTotal: 'إجمالي HyperPay',
    variance: 'الفرق',
    matchedItems: 'متطابقة',
    discrepancies: 'الفروقات',
    unresolved: 'فروقات مفتوحة',
    runs: 'عمليات المطابقة',
    failedRuns: 'عمليات فاشلة',
    uncoveredTitle: '{{count}} يوم في هذه الفترة لم تتم مطابقتها',
    uncoveredBody: '{{payments}} دفعة محصّلة بقيمة {{amount}} لم تُقارن مع HyperPay بعد.',
    runForPeriod: 'مطابقة هذه الفترة',
    runTooLong: 'يمكن مطابقة 31 يوماً كحد أقصى في المرة الواحدة.',
    runDone: 'انتهت المطابقة {{number}}: {{matched}} متطابقة و{{mismatches}} فروقات',
    providerMissing: 'مزود الدفع {{code}} غير مُعدّ.',
    runHistory: 'عمليات المطابقة لهذه الفترة',
    openItems: 'الفروقات المفتوحة',
    showAll: 'إظهار المتطابقة والمعالجة',
    resolveInReconciliation: 'معالجة',
    triggeredBy: 'بواسطة',
    statuses: {
      Created: 'منشأة',
      Pending: 'قيد الانتظار',
      Processing: 'قيد المعالجة',
      Authorized: 'مفوّضة',
      Succeeded: 'ناجحة',
      Failed: 'فاشلة',
      Cancelled: 'ملغاة',
      Expired: 'منتهية',
      Refunded: 'مستردة',
      PartiallyRefunded: 'مستردة جزئياً',
      Voided: 'مُبطلة',
      UnderInvestigation: 'قيد التحقيق',
    },
    methods: {
      Unknown: 'غير معروفة',
      Mada: 'مدى',
      Visa: 'فيزا',
      Mastercard: 'ماستركارد',
      AmericanExpress: 'أمريكان إكسبريس',
      ApplePay: 'Apple Pay',
      GooglePay: 'Google Pay',
      StcPay: 'STC Pay',
      Urpay: 'urpay',
      Sadad: 'سداد',
      BankTransfer: 'تحويل بنكي',
      Cash: 'نقداً',
      Cheque: 'شيك',
    },
    gaps: {
      Any: 'تحتاج متابعة',
      ProviderConfirmation: 'بدون تأكيد من المزود',
      ClientNotification: 'لم يُبلَّغ النظام',
      ReconciliationMismatch: 'فرق مطابقة مفتوح',
      Unreconciled: 'لم تُطابق',
      Unsettled: 'لم تُسوَّ',
    },
    notification: {
      Delivered: 'تم التسليم',
      Failed: 'فشل',
      Retrying: 'إعادة المحاولة',
      Pending: 'بانتظار الإرسال',
      None: 'لا ينطبق',
    },
    outcomes: {
      Matched: 'متطابقة',
      MissingInProvider: 'غير موجودة لدى HyperPay',
      MissingInBarahPay: 'غير موجودة في براح باي',
      AmountMismatch: 'اختلاف المبلغ',
      StatusMismatch: 'اختلاف الحالة',
      Duplicate: 'خصم مكرر',
      RefundMismatch: 'اختلاف الاسترداد',
      SettlementMismatch: 'اختلاف التسوية',
    },
    itemStatuses: {
      Open: 'مفتوح',
      UnderInvestigation: 'قيد التحقيق',
      Resolved: 'تمت المعالجة',
      Accepted: 'مقبول',
    },
    runStatuses: {
      Running: 'جارية',
      Completed: 'مكتملة',
      CompletedWithDiscrepancies: 'بها فروقات',
      Failed: 'فشلت',
    },
  },
  settings: {
    title: 'الإعدادات',
    featureFlags: 'مفاتيح الميزات',
    errorCatalogue: 'كتالوج الأخطاء',
    systemSettings: 'إعدادات النظام',
    category: 'التصنيف',
    value: 'القيمة',
    sensitive: 'حساس',
    updatedBy: 'آخر تعديل بواسطة',
  },
  users: {
    title: 'المستخدمون',
    fullName: 'الاسم الكامل',
    roles: 'الأدوار',
    mfa: 'المصادقة الثنائية',
    lockedOut: 'محظور مؤقتاً',
    lastLogin: 'آخر دخول',
    createUser: 'إضافة مستخدم',
    resetPassword: 'إعادة تعيين كلمة المرور',
    apiClients: 'عملاء الـ API',
    scopes: 'النطاقات',
    rotateSecret: 'تدوير السر',
    secretShownOnce: 'انسخ هذا السر الآن، لن يُعرض مرة أخرى.',
    permissions: 'الصلاحيات',
    createRole: 'إضافة دور',
  },
  paymentLinks: {
    title: 'روابط الدفع',
    create: 'إنشاء رابط',
    linkId: 'الرابط',
    url: 'العنوان',
    amountMode: 'نوع المبلغ',
    fixed: 'مبلغ ثابت',
    customerEntered: 'العميل يحدد المبلغ',
    minAmount: 'الحد الأدنى للمبلغ',
    maxAmount: 'الحد الأعلى للمبلغ',
    singleUse: 'استخدام واحد',
    paymentCount: 'المدفوعات',
    expiresAt: 'تاريخ الانتهاء',
    expire: 'إنهاء الصلاحية',
    copyUrl: 'نسخ الرابط',
    referenceType: 'نوع المرجع',
    referenceId: 'المرجع',
  },
  customers: {
    title: 'العملاء',
    customerNumber: 'رقم العميل',
    name: 'الاسم',
    email: 'البريد الإلكتروني',
    mobile: 'الجوال',
    externalId: 'معرف العميل في المنتج',
    paymentMethods: 'طرق الدفع المحفوظة',
    anonymised: 'مجهول الهوية',
    anonymise: 'إخفاء الهوية (حذف بموجب نظام حماية البيانات)',
    anonymiseWarning: 'يتم استبدال البيانات الشخصية نهائياً. تبقى السجلات المالية كما هي.',
    defaultMethod: 'الافتراضية',
    usable: 'قابلة للاستخدام',
    expired: 'منتهية',
    payments: 'المدفوعات',
    subscriptions: 'الاشتراكات',
  },
  plans: {
    title: 'الخطط',
    code: 'الرمز',
    name: 'الاسم',
    interval: 'دورة الفوترة',
    price: 'السعر',
    monthlyEquivalent: 'المعادل الشهري',
    trialDays: 'أيام التجربة',
    graceDays: 'أيام السماح',
    vatRate: 'نسبة الضريبة',
    customPriced: 'سعر مخصص',
    active: 'نشطة',
    features: 'المزايا',
    create: 'إضافة خطة',
    edit: 'تعديل الخطة',
    sortOrder: 'ترتيب العرض',
  },
  products: {
    title: 'المنتجات',
    code: 'الرمز',
    name: 'الاسم',
    webhookUrl: 'رابط الويب هوك',
    kindBoth: 'كلاهما',
    kindReturn: 'العودة',
    kindCancel: 'الإلغاء',
    webhookSecret: 'سر الويب هوك',
    generateSecret: 'إنشاء سر',
    rotateSecret: 'تدوير السر',
    redirectUrls: 'روابط التحويل المسموحة',
    addRedirectUrl: 'إضافة رابط تحويل',
    allowSubPaths: 'السماح بالمسارات الفرعية',
    kind: 'النوع',
    tenants: 'المستأجرون',
    defaultCurrency: 'العملة الافتراضية',
    expiryMinutes: 'مدة صلاحية الدفع (دقائق)',
    vatRate: 'نسبة الضريبة',
    prorateUpgrades: 'خصم الترقية فوراً',
    statementDescriptor: 'وصف كشف الحساب',
    supportEmail: 'بريد الدعم',
    create: 'إضافة منتج',
    edit: 'تعديل المنتج',
    active: 'نشط',
    secretShownOnce: 'انسخ السر الآن، فهو مخزن مشفراً ولن يُعرض مرة أخرى.',
  },
  billing: {
    title: 'الفواتير',
    invoiceNumber: 'رقم الفاتورة',
    subtotal: 'المجموع قبل الضريبة',
    discount: 'الخصم',
    vat: 'ضريبة القيمة المضافة',
    total: 'الإجمالي',
    amountPaid: 'المدفوع',
    amountDue: 'المستحق',
    dueDate: 'تاريخ الاستحقاق',
    issuedAt: 'تاريخ الإصدار',
    paidAt: 'تاريخ الدفع',
    period: 'الفترة',
    unpaidOnly: 'غير المدفوعة فقط',
    lineItems: 'بنود الفاتورة',
    quantity: 'الكمية',
    unitPrice: 'سعر الوحدة',
    lineTotal: 'إجمالي البند',
    notAccounting: 'فواتير براح باي سجلات داخلية. يبقى ميزان المصدر المحاسبي الرسمي.',
  },
  events: {
    searchPlaceholder: 'EVT-… / الكيان',
    title: 'الأحداث',
    eventNumber: 'رقم الحدث',
    eventType: 'النوع',
    version: 'الإصدار',
    aggregate: 'الكيان',
    occurredAt: 'وقت الحدوث',
    publishedAt: 'وقت النشر',
    nextAttempt: 'المحاولة القادمة',
    lastError: 'آخر خطأ',
    catalogue: 'فهرس الأحداث',
    payload: 'المحتوى',
    retry: 'إعادة المحاولة',
  },
  audit: {
    title: 'سجل التدقيق',
    action: 'الإجراء',
    entityType: 'نوع الكيان',
    entityId: 'الكيان',
    actor: 'المنفذ',
    actorType: 'نوع المنفذ',
    ipAddress: 'عنوان الـ IP',
    oldValue: 'قبل',
    newValue: 'بعد',
    succeeded: 'نجح',
    occurredAt: 'الوقت',
    appendOnly: 'سجلات التدقيق للإضافة فقط ولا يتم تعديلها أو حذفها.',
  },
  health: {
    title: 'حالة النظام',
    environment: 'البيئة',
    version: 'الإصدار',
    database: 'قاعدة البيانات',
    reachable: 'متصلة',
    unreachable: 'غير متصلة',
    eventTransport: 'ناقل الأحداث',
    cacheProvider: 'مزود التخزين المؤقت',
    queues: 'الطوابير والمتأخرات',
    outboxPending: 'صندوق الصادر المعلق',
    outboxFailed: 'صندوق الصادر الفاشل',
    outboxDeadLetter: 'صندوق الصادر الفاشل نهائياً',
    productWebhooksPending: 'إشعارات المنتجات المعلقة',
    productWebhooksDeadLetter: 'إشعارات المنتجات الفاشلة نهائياً',
    notificationsQueued: 'التنبيهات في الطابور',
    webhooksAwaiting: 'إشعارات واردة بانتظار المعالجة',
    openAlerts: 'تنبيهات مفتوحة',
    adapters: 'محولات المزودين المسجلة',
    serverTime: 'وقت الخادم',
    alerts: 'التنبيهات',
    notifications: 'الإشعارات',
    acknowledge: 'إقرار',
    resolveAlert: 'معالجة',
    severity: 'الخطورة',
    raisedAt: 'وقت الإصدار',
    occurrences: 'عدد التكرار',
  },
  investigations: {
    title: 'التحقيقات',
    reason: 'السبب',
    resolution: 'النتيجة',
    openedBy: 'بدأه',
    openedAt: 'تاريخ البدء',
    resolvedBy: 'أغلقه',
    statusAtOpen: 'الحالة عند البدء',
    providerReported: 'ما أبلغ عنه المزود',
    lateSuccessNotice:
      'أبلغ المزود بنجاح العملية بعد انتهاء صلاحيتها. لا تُفقد الأموال بصمت — أغلق التحقيق لتسجيل النتيجة.',
    resolve: 'إغلاق التحقيق',
    confirmProviderSuccess: 'تأكيد نجاح العملية عند المزود',
    confirmProviderFailure: 'تأكيد عدم نجاح العملية عند المزود',
    refundIssued: 'تم الاسترداد للعميل',
    noActionRequired: 'لا يلزم إجراء',
  },
  checkout: {
    payNow: 'ادفع الآن',
    payAmount: 'ادفع {{amount}}',
    amountToPay: 'المبلغ المطلوب',
    enterAmount: 'أدخل المبلغ',
    choosePaymentMethod: 'اختر طريقة الدفع',
    securePayment: 'دفع آمن',
    securedBy: 'تتم معالجة المدفوعات بأمان عبر براح باي.',
    poweredBy: 'مدعوم بواسطة براح باي',
    expiresAt: 'تنتهي صلاحية طلب الدفع',
    reference: 'المرجع',
    paymentSuccessful: 'تم الدفع بنجاح',
    paymentFailed: 'لم تكتمل عملية الدفع',
    paymentPending: 'في انتظار الدفع',
    paymentExpired: 'انتهت صلاحية طلب الدفع',
    paymentCancelled: 'تم إلغاء الدفع',
    underReview: 'عملية الدفع قيد المراجعة',
    returnToMerchant: 'العودة إلى {{merchant}}',
    returningShortly: 'سيتم إرجاعك إلى {{merchant}} تلقائيًا.',
    completedAt: 'وقت الإتمام',
    redirecting: 'جارٍ تحويلك إلى بنكك بشكل آمن',
    preparingPayment: 'جارٍ تجهيز عملية الدفع',
    processingPayment: 'جارٍ معالجة الدفع',
    verifyingPayment: 'جارٍ التحقق من الدفع',
    doNotClose: 'الرجاء عدم إغلاق هذه النافذة أو تحديثها.',
    stillVerifying: 'تستغرق العملية وقتًا أطول من المعتاد. ما زلنا نتحقق من بنكك.',

    notConfirmed: 'لم نتمكّن من تأكيد عملية الدفع بعد',
    doNotPayAgain: 'نرجو عدم الدفع مرة أخرى. إذا كان البنك قد خصم المبلغ فالعملية وصلتنا، والدفع مرتين يعني خصماً مرتين.',
    weWillFollowUp: 'نحن نتحقق من بنكك الآن. إذا تواصلت مع الدعم فاذكر رقم العملية الظاهر بالأسفل.',

    checkAgain: 'التحقق مرة أخرى',
    connectionLost: 'تعذّر الوصول إلى براح باي. تحقّق من اتصالك ثم أعد المحاولة.',
    notAvailable: 'صفحة الدفع غير متوفرة',
    tryAgain: 'إعادة المحاولة',
    contactSupport: 'تواصل مع الدعم',
    minAmount: 'الحد الأدنى {{amount}}',
    maxAmount: 'الحد الأعلى {{amount}}',
    cardDetailsNotice: 'يتم إدخال بيانات البطاقة في صفحة البنك، وليس في براح باي.',
    cardDetailsNoticeEmbedded: 'تنتقل بيانات البطاقة مباشرة إلى مزوّد الدفع، ولا تطّلع عليها براح باي إطلاقًا.',
    widgetUnavailable: 'تعذّر تحميل نموذج الدفع الآمن. يرجى المحاولة مرة أخرى، أو التواصل مع التاجر إذا تكررت المشكلة.',
    noMethodsAvailable: 'لا تتوفر طريقة دفع لهذه العملية حالياً. يرجى المحاولة بعد قليل، أو التواصل مع التاجر.',
    providerTemporarilyUnavailable: 'الدفع غير متاح مؤقتاً. تتحقق هذه الصفحة مرة أخرى تلقائياً كل بضع ثوانٍ، فلا داعي لمغادرتها.',
    previousAttemptFailed: 'لم تكتمل محاولتك السابقة ولم يُخصم أي مبلغ. يمكنك المحاولة مرة أخرى الآن، ببطاقة أخرى إن أردت.',
    noMoneyTaken: 'لم يُخصم أي مبلغ. رابط الدفع ما زال صالحاً، ويمكنك المحاولة مرة أخرى ببطاقة أخرى إن أردت.',
    threeDSecure: 'التحقق الثلاثي',
  },

  fields: {
    adapter: 'المحوّل',
    addEvidence: 'إضافة دليل',
    adapterSettings: 'إعدادات المحوّل',
    adapterSettingsJson: 'إعدادات المحوّل (JSON)',
    adjustments: 'التسويات',
    beginReview: 'بدء المراجعة',
    allowedCallbackIps: 'عناوين IP المسموح بها للإشعارات',
    allowedIps: 'عناوين IP المسموح بها',
    allowedOrigins: 'النطاقات المسموح بها',
    apiKey: 'مفتاح الـ API',
    approval: 'الاعتماد',
    attachment: 'المرفق',
    attachmentReference: 'مرجع المرفق',
    bankValueDate: 'تاريخ القيمة لدى البنك',
    barahPayTransactions: 'معاملات براح باي',
    baseUrl: 'الرابط الأساسي',
    brand: 'العلامة',
    breakerDuration: 'مدة قاطع الدائرة',
    breakerDurationSeconds: 'مدة قاطع الدائرة (ثانية)',
    breakerThreshold: 'حد قاطع الدائرة',
    calculatedNet: 'الصافي المحتسب',
    callbackIps: 'عناوين IP للإشعارات',
    cancelled: 'ملغاة',
    channel: 'القناة',
    chargebacks: 'الاستردادات القسرية',
    clientId: 'معرّف العميل',
    clientSecret: 'سر العميل',
    code: 'الرمز',
    commaSeparated: 'مفصولة بفواصل',
    countSuffix: '× العدد',
    date: 'التاريخ',
    decidedBy: 'صاحب القرار',
    delivered: 'تم التسليم',
    disputeFee: 'رسوم النزاع',
    duration: 'المدة',
    english: 'الإنجليزية',
    enteredBy: 'أدخلها',
    enteredManually: 'يدوي',
    evidence: 'الأدلة',
    evidenceDue: 'موعد تقديم الأدلة',
    evidenceDueSoon: 'موعد تقديم الأدلة قريب',
    failuresOnly: 'الإخفاقات فقط',
    evidenceSubmitted: 'تاريخ تقديم الأدلة',
    expires: 'تنتهي في',
    expiry: 'تاريخ الانتهاء',
    externalId: 'المعرّف الخارجي',
    failed: 'فاشلة',
    headers: 'الترويسات',
    invoice: 'الفاتورة',
    invoiceOrderExample: 'فاتورة، طلب، …',
    justification: 'المبرر',
    key: 'المفتاح',
    keyVersion: 'إصدار المفتاح',
    lastResponse: 'آخر استجابة',
    lastSuccess: 'آخر نجاح',
    lastUsed: 'آخر استخدام',
    latency: 'زمن الاستجابة',
    logoUrl: 'رابط الشعار',
    merchantId: 'معرّف التاجر',
    noAdapter: 'لا يوجد محوّل',
    oneFeaturePerLine: 'ميزة واحدة في كل سطر',
    operation: 'العملية',
    openOnly: 'المفتوحة فقط',
    optional: 'اختياري',
    outcome: 'النتيجة',
    processed: 'تمت المعالجة',
    providerReference: 'مرجع المزوّد',
    providerTransactions: 'معاملات المزوّد',
    rateLimit: 'حد المعدل',
    rateLimitPerMinute: 'حد المعدل / دقيقة',
    received: 'تاريخ الاستلام',
    recipient: 'المستلم',
    reconciled: 'تمت المطابقة',
    recordDispute: 'تسجيل نزاع',
    refundWindowDays: 'مهلة الاسترداد (أيام)',
    refunds: 'المستردات',
    replayedAt: 'تاريخ إعادة الإرسال',
    replayedBy: 'أعاد الإرسال',
    resolved: 'تاريخ الحسم',
    response: 'الاستجابة',
    retries: 'إعادة المحاولة',
    reversalRefund: 'استرداد العكس',
    secretRotated: 'تدوير السر',
    secretsRotated: 'تدوير الأسرار',
    sent: 'تاريخ الإرسال',
    settlementAccountReference: 'مرجع حساب التسوية',
    settlementDate: 'تاريخ التسوية',
    settlementImpact: 'الأثر على التسوية',
    signingKey: 'مفتاح التوقيع',
    sourceFile: 'الملف المصدر',
    sourceIp: 'عنوان IP المصدر',
    start: 'البداية',
    started: 'وقت البدء',
    subject: 'الموضوع',
    submitToProviderNow: 'الإرسال إلى المزوّد الآن',
    succeeded: 'ناجحة',
    successful: 'ناجحة',
    tenant: 'الجهة',
    terminalId: 'معرّف الطرفية',
    timeout: 'المهلة',
    timeoutSeconds: 'المهلة (ثانية)',
    timestampValid: 'الطابع الزمني صالح',
    timing: 'التوقيت',
    toggleNavigation: 'إظهار القائمة أو إخفاؤها',
    tokenLifetimeSeconds: 'عمر الرمز (ثانية)',
    tokenUrl: 'رابط الرمز',
    transactions: 'المعاملات',
    trial: 'الفترة التجريبية',
    triggeredBy: 'من أطلقها',
    type: 'النوع',
    valueDate: 'تاريخ القيمة',
    vatNumber: 'الرقم الضريبي',
    webhookSecret: 'سر الإشعارات',
    webhookTolerance: 'تسامح الإشعارات',
    webhookToleranceSeconds: 'تسامح الإشعارات (ثانية)',
    window: 'النافذة',

    hosted: 'صفحة مستضافة',
    inquiry: 'الاستعلام',
    lastError: 'آخر خطأ',
    links: 'روابط الدفع',
    mitAllowed: 'يسمح بمعاملات يبدأها التاجر',
    refundWindow: 'مهلة الاسترداد',
    settlementAccount: 'حساب التسوية',
    secret: 'السر',
    settlementApi: 'واجهة التسوية',
    supported: 'مدعوم',
    systemRole: 'نظامي',
  },

  notes: {
    featureFlagTakesEffect: 'يسري تغيير الميزة على الطلب التالي؛ لا حاجة لإعادة التشغيل.',
    manualDisputeNeedsApprover: 'يتطلب تسجيل النزاع يدويًا اعتمادًا ثانيًا قبل أن يؤثر على أرقام التسوية.',
    disputeDoesNotAlterPayment:
      'يسجّل النزاع مطالبة البنك، ولا تُمسّ الدفعة الأصلية إطلاقًا — يُقيَّد العكس على شكل استرداد.',
    rolePermissionsAreServerSide:
      'تُطبَّق الصلاحيات في الـ API مع كل طلب. يسري تغيير الدور فورًا دون انتظار انتهاء صلاحية رمز المستخدم.',
    adapterSettingsShape: 'كائن JSON مسطّح؛ يجب أن تكون كل قيمة نصًا.',
  },

  /*
   * Text for rows the platform seeds itself: the nine built-in roles and the ten feature flags.
   *
   * These arrive from the API as English prose written into the database by the seeder, so no
   * resource file could reach them and the Arabic portal showed English sentences in both tables.
   * Keying off the stable role name and flag key translates them without a schema change, and
   * anything unrecognised — a role an operator created, with a description they wrote — keeps the
   * server's text rather than being overwritten by a guess.
   */
  /*
   * The thirty system settings and the categories they group under, both seeded into the database
   * as English prose. Dots in a setting key are a nesting separator to i18next, so the key is
   * stored here with underscores and the lookup swaps them — see translateSettingDescription.
   */
  settingDescriptions: {
    alerts_minimum_sample_size: 'أقل عدد من المدفوعات قبل احتساب نسبة الإخفاق.',
    alerts_payment_failure_rate_percent: 'نسبة الإخفاق التي تُطلق تنبيهًا.',
    disputes_evidence_alert_days: 'عدد الأيام قبل موعد تقديم الأدلة التي يُنبَّه فيها قسم المالية.',
    outbox_max_attempts: 'عدد المحاولات قبل تحويل الحدث إلى قائمة الرسائل الفاشلة.',
    outbox_retry_schedule_seconds: 'سلّم إعادة محاولة نشر الأحداث بالثواني.',
    idempotency_retention_hours: 'مدة اعتماد مفتاح عدم التكرار (٢٤ ساعة كحد أدنى).',
    payments_default_currency: 'العملة المستخدمة عندما لا يحددها الطلب.',
    payments_expiry_minutes: 'عدد الدقائق قبل انتهاء صلاحية دفعة غير مسددة.',
    payments_max_amount: 'أكبر مبلغ لعملية دفع واحدة تقبله المنصة.',
    payments_metadata_max_bytes: 'الحجم الأقصى للبيانات الوصفية للمنتج بعد التحويل.',
    payments_supported_currencies: 'رموز ISO 4217 المقبولة، مفصولة بفواصل.',
    payments_supported_methods: 'طرق الدفع المتاحة على مستوى المنصة.',
    providers_status_sync_after_minutes: 'عدد الدقائق في حالة المعالجة قبل إعادة الاستعلام من المزوّد.',
    providers_timeout_seconds: 'المهلة الافتراضية لطلبات المزوّد.',
    refunds_approval_currency: 'العملة التي يُحتسب بها حد الاعتماد.',
    refunds_approval_threshold: 'المبالغ المستردة عند هذا الحد أو أعلى تتطلب اعتمادًا ثانيًا.',
    retention_audit_days: 'عدد أيام الاحتفاظ بسجلات التدقيق (سبع سنوات).',
    retention_notifications_days: 'عدد أيام الاحتفاظ بسجلات الإشعارات.',
    retention_payments_days: 'يُحتفظ بسجلات المدفوعات ولا تُحذف ما دامت الالتزامات قائمة.',
    retention_webhooks_days: 'عدد أيام الاحتفاظ بحمولات الـ webhooks قبل تنظيفها.',
    subscriptions_grace_days: 'عدد أيام استمرار الخدمة بعد إخفاق التجديد.',
    subscriptions_renewal_lookahead_minutes: 'المدة التي تسبق موعد التجديد التي تبحث فيها المهمة عن الاشتراكات المستحقة.',
    subscriptions_retry_schedule_days: 'سلّم إعادة محاولة التجديد بالأيام بعد تاريخ التجديد.',
    subscriptions_trial_ending_notice_days: 'عدد الأيام قبل انتهاء الفترة التجريبية التي يُشعَر فيها العميل.',
    tax_enabled: 'تطبيق ضريبة القيمة المضافة على فواتير الاشتراكات.',
    tax_vat_rate: 'نسبة ضريبة القيمة المضافة الافتراضية على الفواتير.',
    urls_admin_base: 'عنوان استضافة لوحة الإدارة.',
    urls_checkout_base: 'عنوان استضافة صفحة الدفع العامة.',
    webhooks_max_attempts: 'عدد المحاولات قبل تحويل webhook المنتج إلى قائمة الرسائل الفاشلة.',
    webhooks_retry_schedule_minutes: 'سلّم إعادة محاولة إرسال webhooks المنتجات بالدقائق.',
  },

  settingCategories: {
    Alerting: 'التنبيهات',
    Disputes: 'الاعتراضات',
    Events: 'الأحداث',
    Payments: 'المدفوعات',
    Providers: 'مزوّدو الدفع',
    Refunds: 'المبالغ المستردة',
    Retention: 'مدد الاحتفاظ',
    Subscriptions: 'الاشتراكات',
    Tax: 'الضريبة',
    Urls: 'العناوين',
    Webhooks: 'الـ Webhooks',
  },

  roleDescriptions: {
    'Super Admin': 'صلاحية كاملة على جميع أجزاء المنصة.',
    'Finance Admin': 'العمليات المالية شاملةً المبالغ المستردة والتسويات والمطابقة.',
    'Finance Viewer': 'اطّلاع فقط على البيانات المالية والتقارير.',
    'Payment Operations': 'عمليات الدفع اليومية والتحقيقات.',
    'Refund Approver': 'اعتماد المبالغ المستردة التي تتجاوز الحد المحدد.',
    'Reconciliation Officer': 'تشغيل عمليات المطابقة ومعالجة فروقاتها.',
    'Technical Support': 'تشخيص مشكلات المدفوعات والـ webhooks.',
    Developer: 'إدارة المنتجات وعملاء الـ API وإعدادات التكامل.',
    Auditor: 'اطّلاع فقط على سجلات التدقيق والسجلات المالية.',
  },

  flagDescriptions: {
    EnableRecurringPayments: 'خصم الرموز المحفوظة لتجديد الاشتراكات.',
    EnableApplePay: 'إتاحة Apple Pay في صفحة الدفع.',
    EnablePartialRefund: 'السماح باسترداد مبلغ أقل من قيمة الدفعة كاملة.',
    EnableProviderRouting: 'تطبيق قواعد توجيه المزوّدين بدلًا من استخدام المزوّد الافتراضي.',
    EnableMakerChecker: 'اشتراط اعتماد ثانٍ للعمليات عالية المخاطر.',
    EnableNewCheckout: 'تقديم تجربة صفحة الدفع الحالية.',
    EnableManualPayments: 'السماح للمشغّلين بتسجيل مدفوعات الحوالات البنكية وسداد والنقد والشيكات.',
    EnableProductWebhooks: 'إرسال webhooks موقّعة إلى المنتجات إضافةً إلى ناقل الأحداث.',
    EnableSubscriptionProration: 'احتساب الفرق تناسبيًا عند الترقية الفورية للباقة.',
    EnableEmbeddedCheckout: 'السماح للمزوّد بعرض نموذج الدفع داخل صفحة الدفع بدلًا من التحويل.',
  },

  /*
   * Text for every code in the Barah Pay error catalogue (spec section 52), plus the three
   * client-side conditions the API never reports because the request never reached it.
   *
   * The API returns a stable code and an English detail line; it does not translate. Rendering the
   * detail put an English sentence under an Arabic heading on every failure, so the portal keys off
   * the code instead and the server text becomes a diagnostic rather than the message.
   */
  errors: {
    PAYMENT_DECLINED: 'تم رفض عملية الدفع. جرّب بطاقة أخرى أو تواصل مع بنكك.',
    PAYMENT_EXPIRED: 'انتهت صلاحية طلب الدفع.',
    PAYMENT_NOT_FOUND: 'تعذّر العثور على عملية الدفع.',
    PAYMENT_ALREADY_COMPLETED: 'تم إتمام عملية الدفع مسبقًا.',
    PAYMENT_NOT_CANCELLABLE: 'لم يعد بالإمكان إلغاء عملية الدفع.',
    PAYMENT_AMOUNT_INVALID: 'قيمة الدفع غير صحيحة.',
    PAYMENT_UNDER_INVESTIGATION: 'عملية الدفع قيد التحقيق ولا يمكن تعديلها.',
    INSUFFICIENT_FUNDS: 'الرصيد غير كافٍ لإتمام عملية الدفع.',
    INVALID_PAYMENT_METHOD: 'طريقة الدفع غير متاحة لهذه العملية.',
    INVALID_STATE_TRANSITION: 'هذا الإجراء غير مسموح به في الحالة الراهنة لعملية الدفع.',
    REFERENCE_LIMIT_EXCEEDED: 'تم استخدام هذا المرجع عددًا كبيرًا من المرات.',
    AUTHENTICATION_FAILED_3DS: 'لم يكتمل التحقق من البطاقة.',
    PROVIDER_UNAVAILABLE: 'مزوّد الدفع غير متوفر مؤقتًا. أعد المحاولة بعد قليل.',
    PROVIDER_TIMEOUT: 'لم يستجب مزوّد الدفع في الوقت المحدد. أعد المحاولة بعد قليل.',
    PROVIDER_ERROR: 'أعاد مزوّد الدفع خطأ. أعد المحاولة بعد قليل.',
    PROVIDER_NOT_CONFIGURED: 'لا توجد بيانات اعتماد مهيأة لهذا المزوّد في البيئة الحالية.',
    CAPABILITY_NOT_SUPPORTED: 'المزوّد لا يدعم هذه العملية.',
    REFUND_NOT_ALLOWED: 'لا يمكن استرداد هذه الدفعة.',
    REFUND_AMOUNT_EXCEEDS_PAYMENT: 'مبلغ الاسترداد أكبر من المبلغ المتبقي على الدفعة.',
    REFUND_NOT_FOUND: 'تعذّر العثور على عملية الاسترداد.',
    REFUND_APPROVAL_REQUIRED: 'يتطلب هذا الاسترداد اعتمادًا ثانيًا قبل معالجته.',
    REFUND_ALREADY_PROCESSED: 'تمت معالجة هذا الاسترداد مسبقًا.',
    VOID_NOT_ALLOWED: 'لا يمكن إلغاء هذا التفويض.',
    SUBSCRIPTION_NOT_FOUND: 'تعذّر العثور على الاشتراك.',
    SUBSCRIPTION_PAST_DUE: 'دفعة الاشتراك متأخرة.',
    SUBSCRIPTION_NOT_ACTIVE: 'هذا الاشتراك غير نشط.',
    SUBSCRIPTION_ALREADY_CANCELLED: 'تم إلغاء هذا الاشتراك مسبقًا.',
    PLAN_NOT_FOUND: 'تعذّر العثور على الباقة.',
    PLAN_NOT_ACTIVE: 'هذه الباقة غير نشطة.',
    PAYMENT_METHOD_TOKEN_MISSING: 'لا توجد وسيلة دفع محفوظة لهذا الاشتراك.',
    RECURRING_NOT_SUPPORTED: 'المزوّد لا يدعم المدفوعات المتكررة.',
    PAYMENT_LINK_NOT_FOUND: 'تعذّر العثور على رابط الدفع.',
    PAYMENT_LINK_EXPIRED: 'انتهت صلاحية رابط الدفع.',
    PAYMENT_LINK_ALREADY_USED: 'تم استخدام رابط الدفع مسبقًا.',
    DUPLICATE_REQUEST: 'تم إرسال هذا الطلب مسبقًا.',
    IDEMPOTENCY_KEY_CONFLICT: 'استُخدم مفتاح عدم التكرار هذا مع طلب مختلف.',
    IDEMPOTENCY_REQUEST_IN_PROGRESS: 'ما زال طلب مطابق قيد المعالجة. انتظر قليلًا ثم تحقّق من النتيجة.',
    VALIDATION_FAILED: 'الطلب غير صالح. راجع الحقول المحددة.',
    CONCURRENCY_CONFLICT: 'قام مستخدم آخر بتعديل هذا السجل. حدّث الصفحة ثم أعد المحاولة.',
    RATE_LIMIT_EXCEEDED: 'عدد كبير من الطلبات. انتظر قليلًا ثم أعد المحاولة.',
    METADATA_INVALID: 'البيانات الوصفية غير صالحة.',
    CURRENCY_NOT_SUPPORTED: 'العملة غير مدعومة.',
    REDIRECT_URL_NOT_ALLOWED: 'رابط العودة هذا غير مسجَّل لهذا المنتج.',
    UNAUTHORIZED: 'انتهت جلستك. الرجاء تسجيل الدخول مرة أخرى.',
    FORBIDDEN: 'هذه العملية غير مسموح بها.',
    SCOPE_MISSING: 'لا يملك عميل الـ API النطاق اللازم لهذه العملية.',
    PERMISSION_MISSING: 'لا تملك الصلاحية اللازمة لهذه العملية.',
    INVALID_CREDENTIALS: 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
    MFA_REQUIRED: 'مطلوب التحقق بخطوتين.',
    MFA_INVALID: 'رمز التحقق غير صحيح.',
    TOKEN_INVALID: 'الرمز غير صالح أو انتهت صلاحيته.',
    PRODUCT_NOT_FOUND: 'تعذّر العثور على المنتج.',
    PRODUCT_INACTIVE: 'هذا المنتج غير نشط.',
    CUSTOMER_NOT_FOUND: 'تعذّر العثور على العميل.',
    TENANT_NOT_FOUND: 'تعذّر العثور على المستأجر.',
    SETTING_NOT_FOUND: 'تعذّر العثور على الإعداد.',
    FEATURE_DISABLED: 'هذه الميزة موقوفة.',
    APPROVAL_REQUIRED: 'يتطلب هذا التغيير اعتمادًا ثانيًا.',
    APPROVAL_NOT_ALLOWED: 'لا يمكنك اعتماد هذا الطلب.',
    SELF_APPROVAL_NOT_ALLOWED: 'لا يمكنك اعتماد طلب قمت بإنشائه بنفسك.',
    WEBHOOK_SIGNATURE_INVALID: 'تعذّر التحقق من توقيع الاستدعاء.',
    WEBHOOK_TIMESTAMP_INVALID: 'الطابع الزمني للاستدعاء خارج النطاق المقبول.',
    WEBHOOK_REPLAY_DETECTED: 'تم استلام هذا الاستدعاء مسبقًا.',
    WEBHOOK_NOT_FOUND: 'تعذّر العثور على الـ webhook.',
    SETTLEMENT_NOT_FOUND: 'تعذّر العثور على التسوية.',
    SETTLEMENT_FILE_INVALID: 'تعذّرت قراءة ملف التسوية.',
    RECONCILIATION_NOT_FOUND: 'تعذّر العثور على عملية المطابقة.',
    DISPUTE_NOT_FOUND: 'تعذّر العثور على الاعتراض.',
    DISPUTE_ALREADY_RESOLVED: 'تمت تسوية هذا الاعتراض مسبقًا.',
    INTERNAL_ERROR: 'حدث خطأ لدينا. أعد المحاولة بعد قليل.',
    NETWORK_ERROR: 'تعذّر الوصول إلى براح باي. تحقّق من اتصالك ثم أعد المحاولة.',
    REQUEST_TIMEOUT: 'استغرق الطلب وقتًا طويلًا. أعد المحاولة.',
    UNKNOWN: 'حدث خطأ. أعد المحاولة.',
  },
};

export const resources = {
  en: { translation: en },
  ar: { translation: ar },
};

export type TranslationResources = typeof en;

/** Creates the shared i18next instance. Each app calls this once at start-up. */
export function createI18n(initialLanguage: string = 'en'): i18n {
  const language = SUPPORTED_LANGUAGES.includes(initialLanguage as SupportedLanguage)
    ? initialLanguage
    : 'en';

  if (!i18next.isInitialized) {
    // initReactI18next registers the instance with react-i18next; without it `useTranslation` has no
    // instance to read and every component renders the raw key instead of the translation.
    void i18next.use(initReactI18next).init({
      resources,
      lng: language,
      fallbackLng: 'en',
      interpolation: { escapeValue: false },
      returnNull: false,
    });
  }

  applyDocumentDirection(language);
  return i18next;
}

export { i18next };
